import React, { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuItem from '@mui/material/MenuItem';
import { Select } from '@mui/material';
import dayjs from 'dayjs';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import classes from './EditTimesheetForm.module.scss';
import TimesheetItem from './TimesheetItem';
import { getDatesForWeek, getHoursByDate } from '../../../helpers';
import { getNumberWithDelimeter, isValidByRateType } from '../../../../../../utils/helpers';
import SearchLocation from '../../../../../common/SearchLocation/SearchLocation';
import StyledLocationContainer from './StyledLocationContainer';
import ValidationErrorLabel from '../../../../../common/ValidationErrorLabel/ValidationErrorLabel';
import { qtyAllowedForComment, qtyToShowComment } from '../../../../../../utils/constants';
import NotificationModal from '../../../../../common/ModalWindows/NotificationModal';
import { rateEnum, submissionEnum } from '../../../../../../utils/enums';

const textFieldStyles = {
  width: '100%',
  backgroundColor: 'white',
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #D1D3D4',
    borderRadius: '10px'
  },
  color: '#283237',
  fontSize: '14px',
  fontWeight: '325',
  lineHeight: '17px',
  fieldset: {
    legend: {
      width: 'unset'
    }
  }
};

const selectStyles = {
  width: '100%',
  height: '40px',
  paddingLeft: '20px',
  backgroundColor: 'white',
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #D1D3D4',
    borderRadius: '10px'
  },
  '& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input': {
    padding: '0 32px 0 0'
  },
  '& .MuiSvgIcon-root': {
    color: '#283237',
    fontSize: '18px',
    right: '22px'
  },
  color: '#283237',
  fontSize: '14px',
  fontWeight: '325',
  lineHeight: '17px',
  fieldset: {
    legend: {
      width: 'unset'
    }
  }
};

export default function EditTimesheetForm(props) {
  const {
    isComplexWorktype = false,
    formData,
    startDateOfPeriod,
    allowedWorkTypes,
    choosedMonth = 0,
    timesheetType = submissionEnum.WEEKLY,
    rateType = rateEnum.HOURLY,
    withErrors = true,
    handleWhenCleared = () => {},
    extraComponent
  } = props;

  const { t } = useTranslation();
  const [openRateError, setOpenRateError] = useState('');

  const datesForWeek = getDatesForWeek(startDateOfPeriod);

  return (
    <section className={classes.mainContainer}>
      <div
        style={{
          width: '100%',
          height: formData.errors.Location ? 'max-content' : '40px',
          padding: '6px 10px 6px 0px'
        }}
      >
        <StyledLocationContainer>
          <SearchLocation
            value={formData.values?.Location}
            changeHandler={(value) => formData.setFieldValue('Location', value)}
            handleWhenCleared={handleWhenCleared}
            withClear={false}
            placeholder={`${t('dashboard.timesheet.table.location')} *`}
          />
        </StyledLocationContainer>
        {withErrors && formData.errors.Location && <ValidationErrorLabel message={formData.errors.Location} />}
      </div>
      <div
        style={{
          width: '100%',
          height: formData.errors.WorkTypeId ? 'unset' : '40px',
          padding: '6px 10px 6px 0px'
        }}
      >
        {isComplexWorktype ? (
          <Select
            value={allowedWorkTypes.find((type) => type.Id === formData.values?.WorkType.Id)}
            sx={selectStyles}
            IconComponent={ExpandMoreIcon}
            onChange={(e) => {
              formData.setFieldValue(
                'WorkType',
                allowedWorkTypes.find((type) => type.Id === e.target.value)
              );
            }}
            displayEmpty
            renderValue={
              formData.values?.WorkType.Id !== ''
                ? () => formData.values?.WorkType.Name
                : () => `${t('dashboard.timesheet.table.worktype')}*`
            }
          >
            {allowedWorkTypes.map((type) => (
              <MenuItem key={type.Id} value={type.Id}>
                {type.Name}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Select
            value={allowedWorkTypes.find((type) => type.value === formData.values?.WorkTypeId)?.value}
            sx={selectStyles}
            IconComponent={ExpandMoreIcon}
            onChange={(e) => {
              formData.setFieldValue('WorkTypeId', e.target.value);
            }}
            displayEmpty
            renderValue={
              formData.values?.WorkTypeId !== '' ? undefined : () => `${t('dashboard.timesheet.table.worktype')}*`
            }
          >
            {allowedWorkTypes.map((type) => (
              <MenuItem key={type.key} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        )}
        {withErrors && formData.errors.WorkTypeId && <ValidationErrorLabel message={formData.errors.WorkTypeId} />}
      </div>
      <div className={classes.timesheetContainer}>
        {datesForWeek.map((date) => (
          <TimesheetItem
            key={date}
            disabled={date.month() !== choosedMonth && timesheetType === submissionEnum.MONTHLY}
            day={dayjs(date).format('ddd DD')}
            value={getNumberWithDelimeter(getHoursByDate(formData.values?.TimesheetHoursByDates, date))}
            changeHandler={(newValue) => {
              if (!isValidByRateType(newValue, rateType)) {
                setOpenRateError(
                  rateType === rateEnum.HOURLY
                    ? t('dashboard.timesheet.validations.max24h')
                    : t('dashboard.timesheet.validations.max1day')
                );
              }
              if (
                formData.values?.TimesheetHoursByDates.some((item) => {
                  const date1 = {
                    year: new Date(item.Day).getFullYear(),
                    month: new Date(item.Day).getMonth(),
                    d: new Date(item.Day).getDate()
                  };
                  const date2 = {
                    year: date.year(),
                    month: date.month(),
                    d: date.date()
                  };
                  return date1.year === date2.year && date1.month === date2.month && date1.d === date2.d;
                })
              ) {
                formData.setFieldValue(
                  'TimesheetHoursByDates',
                  formData.values?.TimesheetHoursByDates.map((value) => {
                    return dayjs(value.Day.toString()).isSame(date)
                      ? {
                          Day: value.Day,
                          TotalHrs: isValidByRateType(newValue, rateType) ? Number(newValue) : value.TotalHrs
                        }
                      : value;
                  })
                );
              } else {
                formData.setFieldValue('TimesheetHoursByDates', [
                  ...formData.values.TimesheetHoursByDates,
                  { Day: date.toString(), TotalHrs: isValidByRateType(newValue, rateType) ? newValue : 0 }
                ]);
              }
            }}
          />
        ))}
      </div>
      {extraComponent}
      <TextField
        multiline
        minRows={3}
        maxRows={5}
        placeholder={t('dashboard.timesheet.table.comment')}
        value={formData.values?.Comment}
        onChange={(e) => {
          if (e.target.value.length <= qtyAllowedForComment) formData.setFieldValue('Comment', e.target.value);
        }}
        sx={textFieldStyles}
      />
      {formData.values?.Comment.length >= qtyToShowComment && (
        <p className={classes.counter}>{`${formData.values?.Comment.length}/${qtyAllowedForComment}`}</p>
      )}
      {openRateError && (
        <NotificationModal open={openRateError} onClose={() => setOpenRateError('')}>
          <p className={classes.errorMessage}>{openRateError}</p>
        </NotificationModal>
      )}
    </section>
  );
}
